import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Card, Input, Button, Space, Select, Tag, Progress, Switch, Collapse, Spin, Divider, Typography, Alert } from 'antd';
import { SendOutlined, CloseOutlined, HistoryOutlined, ThunderboltOutlined, LoadingOutlined, MessageOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';

const { TextArea } = Input;
const { Text } = Typography;
const DEFAULT_MODEL = 'DeepSeek-V4-Pro';
const MODEL_OPTIONS = [
  { value: DEFAULT_MODEL, label: 'DeepSeek V4 Pro' },
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' }
];
const MODEL_VALUES = new Set(MODEL_OPTIONS.map(option => option.value));

function initialModel() {
  const stored = localStorage.getItem('bazi-ai-model');
  return MODEL_VALUES.has(stored) ? stored : DEFAULT_MODEL;
}

export default function AiPanel({ markdown, analysisContext, onClose, recordId, username, savedAnalyses, disabled = false }) {
  const [question, setQuestion] = useState('请分析这个八字的整体格局、用神、性格特点和运势走向。');
  const [thinking, setThinking] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [followupQuestion, setFollowupQuestion] = useState('');
  const [followupDraft, setFollowupDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [activeFollowupAnalysisId, setActiveFollowupAnalysisId] = useState(null);
  const [taskInfo, setTaskInfo] = useState(null);
  const [useMaxReasoning, setUseMaxReasoning] = useState(false);
  const [model, setModel] = useState(initialModel);
  const [analyses, setAnalyses] = useState(savedAnalyses || []);
  const [error, setError] = useState('');
  const conclusionRef = useRef(null);
  const pollingRef = useRef(null);
  const requestRef = useRef(null);
  const taskGenerationRef = useRef(0);
  const restoredTaskRef = useRef(null);
  const activeTaskStorageKey = useMemo(() => {
    if (!username || !recordId) return null;
    return `bazi-active-task:${username}:${recordId}`;
  }, [username, recordId]);

  useEffect(() => {
    setAnalyses(savedAnalyses || []);
  }, [savedAnalyses]);

  useEffect(() => {
    localStorage.setItem('bazi-ai-model', model);
  }, [model]);

  useEffect(() => {
    if (conclusionRef.current) {
      conclusionRef.current.scrollTop = conclusionRef.current.scrollHeight;
    }
  }, [conclusion, followupDraft]);

  const splitContent = useCallback((analysis) => {
    if (analysis.conclusion) {
      return { thinking: analysis.thinking || '', conclusion: analysis.conclusion };
    }
    const content = analysis.content || '';
    const markers = ['### 📌 第7步', '### 📌 第7步：', 'Step 6：综合交叉验证'];
    for (const marker of markers) {
      const idx = content.indexOf(marker);
      if (idx !== -1) {
        return { thinking: content.substring(0, idx).trim(), conclusion: content.substring(idx).trim() };
      }
    }
    return { thinking: '', conclusion: content };
  }, []);

  const loadAnalysis = useCallback((analysis, idx = null) => {
    if (!analysis) return;
    const { thinking: t, conclusion: c } = splitContent(analysis);
    setSelectedIdx(idx);
    setSelectedAnalysisId(analysis.id || null);
    if (MODEL_VALUES.has(analysis.model)) setModel(analysis.model);
    setThinking(t);
    setConclusion(c);
    setFollowupDraft('');
    setActiveFollowupAnalysisId(null);
  }, [splitContent]);

  const selectedAnalysis = useMemo(() => (
    selectedAnalysisId
      ? analyses.find(analysis => analysis.id === selectedAnalysisId) || null
      : null
  ), [analyses, selectedAnalysisId]);

  const saveActiveTask = useCallback((task) => {
    if (!activeTaskStorageKey) return;
    try {
      localStorage.setItem(activeTaskStorageKey, JSON.stringify({
        ...task,
        username,
        record_id: recordId,
        saved_at: new Date().toISOString()
      }));
    } catch (error) {
      console.warn('保存后台任务状态失败', error);
    }
  }, [activeTaskStorageKey, username, recordId]);

  const clearActiveTask = useCallback(() => {
    if (!activeTaskStorageKey) return;
    try {
      localStorage.removeItem(activeTaskStorageKey);
    } catch (error) {
      console.warn('清理后台任务状态失败', error);
    }
  }, [activeTaskStorageKey]);

  const cancelInFlight = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    requestRef.current?.abort();
    requestRef.current = null;
    taskGenerationRef.current += 1;
  }, []);

  const finishTask = useCallback(() => {
    if (pollingRef.current) clearTimeout(pollingRef.current);
    pollingRef.current = null;
    requestRef.current = null;
    clearActiveTask();
    setLoading(false);
    setTaskInfo(null);
    setActiveFollowupAnalysisId(null);
  }, [clearActiveTask]);

  const pollTask = useCallback((taskId, generation) => {
    const pollOnce = async () => {
      if (taskGenerationRef.current !== generation) return;
      const controller = new AbortController();
      requestRef.current = controller;
      try {
        const res = await fetch(`/api/bazi/analyze/${taskId}`, { signal: controller.signal });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `获取分析进度失败（${res.status}）`);
        if (taskGenerationRef.current !== generation) return;
        if (data.status === 'error') throw new Error(data.error || data.message || '分析任务失败');

        setTaskInfo({ step: data.current_step, total: data.total_steps, mode: data.mode });

        if (data.mode === 'followup') {
          setFollowupDraft(data.followup_answer || '');
        } else {
          setThinking(data.thinking || '');
          setConclusion(data.conclusion || '');
        }

        if (data.status === 'done') {
          if (data.mode === 'followup' && data.saved_followup && data.analysis_id) {
            setAnalyses(prev => prev.map((analysis) => {
              if (analysis.id !== data.analysis_id) return analysis;
              return {
                ...analysis,
                followups: [...(analysis.followups || []), data.saved_followup]
              };
            }));
            if (selectedAnalysisId === data.analysis_id) {
              setFollowupDraft('');
            }
            setFollowupQuestion('');
          } else if (data.mode === 'analysis' && data.saved_analysis) {
            setAnalyses(prev => [...prev, data.saved_analysis]);
            loadAnalysis(data.saved_analysis);
          }
          finishTask();
          return;
        }
        pollingRef.current = setTimeout(pollOnce, 1500);
      } catch (pollError) {
        if (pollError.name === 'AbortError' || taskGenerationRef.current !== generation) return;
        setError(pollError.message || '分析任务失败');
        finishTask();
      }
    };
    pollOnce();
  }, [finishTask, loadAnalysis, selectedAnalysisId]);

  useEffect(() => {
    if (analyses.length > 0 && selectedIdx == null && !conclusion && !thinking && !loading) {
      loadAnalysis(analyses[analyses.length - 1], analyses.length - 1);
    }
  }, [analyses, selectedIdx, conclusion, thinking, loading, loadAnalysis]);

  useEffect(() => cancelInFlight, [cancelInFlight]);

  useEffect(() => {
    if (!activeTaskStorageKey || loading || pollingRef.current) return;
    let storedTask = null;
    try {
      const raw = localStorage.getItem(activeTaskStorageKey);
      storedTask = raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn('读取后台任务状态失败', error);
      clearActiveTask();
      return;
    }
    if (!storedTask?.task_id || storedTask.record_id !== recordId) return;
    const restoreKey = `${activeTaskStorageKey}:${storedTask.task_id}`;
    if (restoredTaskRef.current === restoreKey) return;
    restoredTaskRef.current = restoreKey;

    if (storedTask.mode === 'followup') {
      setActiveFollowupAnalysisId(storedTask.analysis_id || null);
      setSelectedAnalysisId(storedTask.analysis_id || null);
      setFollowupQuestion(storedTask.followupQuestion || storedTask.question || '');
      setFollowupDraft('');
      const idx = analyses.findIndex(analysis => analysis.id === storedTask.analysis_id);
      if (idx !== -1) {
        loadAnalysis(analyses[idx], idx);
        setActiveFollowupAnalysisId(storedTask.analysis_id || null);
      }
      setTaskInfo({ step: 0, total: 1, mode: 'followup' });
    } else {
      setQuestion(storedTask.question || question);
      setThinking('');
      setConclusion('');
      setFollowupDraft('');
      setSelectedIdx(null);
      setSelectedAnalysisId(null);
      setActiveFollowupAnalysisId(null);
      setTaskInfo({ step: 0, total: 7, mode: 'analysis' });
    }
    if (MODEL_VALUES.has(storedTask.model)) setModel(storedTask.model);
    setError('');
    setLoading(true);
    const generation = taskGenerationRef.current + 1;
    taskGenerationRef.current = generation;
    pollTask(storedTask.task_id, generation);
  }, [activeTaskStorageKey, analyses, clearActiveTask, loadAnalysis, loading, pollTask, question, recordId]);

  const handleAnalyze = async () => {
    if (loading || disabled) return;
    cancelInFlight();
    const generation = taskGenerationRef.current + 1;
    taskGenerationRef.current = generation;
    setLoading(true);
    setError('');
    setThinking('');
    setConclusion('');
    setFollowupDraft('');
    setSelectedIdx(null);
    setSelectedAnalysisId(null);
    setActiveFollowupAnalysisId(null);
    setTaskInfo({ step: 0, total: 7, mode: 'analysis' });
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const res = await fetch('/api/bazi/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          markdown,
          question,
          username,
          record_id: recordId,
          analysis_context: analysisContext,
          model,
          reasoning_effort: useMaxReasoning ? 'max' : 'high'
        }),
        signal: controller.signal
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `提交分析任务失败（${res.status}）`);
      if (!data.task_id) throw new Error(data.error || '提交分析任务失败：响应缺少任务编号');
      if (taskGenerationRef.current !== generation) return;
      saveActiveTask({
        task_id: data.task_id,
        mode: 'analysis',
        question,
        model,
        record_id: recordId
      });
      pollTask(data.task_id, generation);
    } catch (requestError) {
      if (requestError.name === 'AbortError' || taskGenerationRef.current !== generation) return;
      setError(requestError.message || '提交分析任务失败');
      clearActiveTask();
      setLoading(false);
      setTaskInfo(null);
    }
  };

  const handleFollowup = async () => {
    if (loading || disabled || !selectedAnalysis?.id || !followupQuestion.trim()) return;
    cancelInFlight();
    const generation = taskGenerationRef.current + 1;
    taskGenerationRef.current = generation;
    setLoading(true);
    setError('');
    setFollowupDraft('');
    setActiveFollowupAnalysisId(selectedAnalysis.id);
    setTaskInfo({ step: 0, total: 1, mode: 'followup' });
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const res = await fetch('/api/bazi/followup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          record_id: recordId,
          analysis_id: selectedAnalysis.id,
          question: followupQuestion.trim(),
          analysis_context: analysisContext,
          model,
          reasoning_effort: useMaxReasoning ? 'max' : 'high'
        }),
        signal: controller.signal
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `提交追问失败（${res.status}）`);
      if (!data.task_id) throw new Error(data.error || '提交追问失败：响应缺少任务编号');
      if (taskGenerationRef.current !== generation) return;
      saveActiveTask({
        task_id: data.task_id,
        mode: 'followup',
        record_id: recordId,
        analysis_id: selectedAnalysis.id,
        model,
        followupQuestion: followupQuestion.trim()
      });
      pollTask(data.task_id, generation);
    } catch (requestError) {
      if (requestError.name === 'AbortError' || taskGenerationRef.current !== generation) return;
      setError(requestError.message || '提交追问失败');
      clearActiveTask();
      setLoading(false);
      setTaskInfo(null);
      setActiveFollowupAnalysisId(null);
    }
  };

  const handleClose = () => {
    cancelInFlight();
    onClose();
  };

  const handleSelectHistory = (idx) => {
    loadAnalysis(analyses[idx], idx);
  };

  const progressPercent = taskInfo ? Math.round((taskInfo.step / taskInfo.total) * 100) : 0;
  const isThinking = loading && taskInfo?.mode === 'analysis' && taskInfo.step < taskInfo.total;
  const thinkingLabel = taskInfo
    ? `思考过程（${taskInfo.step}/${taskInfo.total}步）`
    : '思考过程（分析步骤详情）';
  const currentFollowupDraft = selectedAnalysis?.id === activeFollowupAnalysisId ? followupDraft : '';

  return (
    <Card
      title="🔮 AI 八字分析（六步法深度分析）"
      extra={<Button type="text" icon={<CloseOutlined />} onClick={handleClose} aria-label="关闭 AI 分析" />}
      style={{ marginBottom: 16 }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
        {analyses.length > 0 && (
          <div>
            <HistoryOutlined /> <span style={{ marginRight: 8, fontSize: 13, color: '#666' }}>历史分析：</span>
            <Select
              value={selectedIdx}
              onChange={handleSelectHistory}
              style={{ width: '100%' }}
              placeholder="选择历史分析记录"
            >
              {analyses.map((a, i) => (
                <Select.Option key={a.id || i} value={i}>
                  <Tag color="blue" style={{ marginRight: 4 }}>{i + 1}</Tag>
                  {a.question || '全面分析'} — {a.model || '旧版本'} — {a.created_at}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}

        <TextArea
          rows={2}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder="输入你想问的问题（留空则进行全面分析）..."
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Select
            aria-label="分析模型"
            value={model}
            options={MODEL_OPTIONS}
            onChange={setModel}
            disabled={loading || disabled}
            style={{ width: 180 }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={loading && taskInfo?.mode === 'analysis'}
            disabled={disabled || loading}
            onClick={handleAnalyze}
          >
            {loading && taskInfo?.mode === 'analysis' ? '分析中...' : '开始新分析'}
          </Button>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#666' }}>
            <ThunderboltOutlined />
            <Switch size="small" checked={useMaxReasoning} onChange={setUseMaxReasoning} disabled={loading || disabled} />
            <span>{useMaxReasoning ? '深度推理' : '标准'}</span>
          </span>
          {loading && <span style={{ fontSize: 12, color: '#999' }}>关闭页面不影响，后台持续运行</span>}
        </div>

        {(thinking || isThinking) && (
          <Collapse
            size="small"
            items={[{
              key: 'thinking',
              label: (
                <span style={{ color: '#666' }}>
                  {isThinking && <Spin indicator={<LoadingOutlined spin style={{ fontSize: 14, marginRight: 6 }} />} />}
                  {isThinking ? `🧠 正在思考... ${thinkingLabel}` : `🧠 ${thinkingLabel}`}
                  {loading && taskInfo?.mode === 'analysis' && (
                    <Progress
                      percent={progressPercent}
                      steps={taskInfo.total}
                      size="small"
                      format={() => `${taskInfo.step}/${taskInfo.total}`}
                      style={{ marginLeft: 12, display: 'inline-flex' }}
                    />
                  )}
                </span>
              ),
              children: (
                <div style={{
                  maxHeight: 400, overflow: 'auto', lineHeight: 1.8,
                  wordBreak: 'break-word', fontSize: 13, color: '#555'
                }}>
                  <ReactMarkdown>{thinking}</ReactMarkdown>
                </div>
              )
            }]}
          />
        )}

        {conclusion && (
          <div ref={conclusionRef} style={{
            background: '#fff', border: '1px solid #d9d9d9', borderRadius: 8,
            padding: '12px 16px', maxHeight: 600, overflow: 'auto', lineHeight: 1.8,
            wordBreak: 'break-word'
          }}>
            <ReactMarkdown>{conclusion}</ReactMarkdown>
          </div>
        )}

        {selectedAnalysis && (selectedAnalysis.followups?.length > 0 || currentFollowupDraft) && (
          <div style={{
            background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <MessageOutlined />
              <Text strong>结果追问</Text>
            </div>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {(selectedAnalysis.followups || []).map((item) => (
                <div key={item.id || item.created_at}>
                  <div style={{ background: '#e6f4ff', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <Text strong>你：</Text> {item.question}
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 10 }}>
                    <ReactMarkdown>{item.answer}</ReactMarkdown>
                  </div>
                </div>
              ))}
              {currentFollowupDraft && (
                <div>
                  <div style={{ background: '#e6f4ff', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <Text strong>你：</Text> {followupQuestion}
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 10 }}>
                    {loading && taskInfo?.mode === 'followup' && (
                      <div style={{ color: '#999', marginBottom: 8 }}>
                        <Spin indicator={<LoadingOutlined spin style={{ fontSize: 14, marginRight: 6 }} />} />
                        正在回答追问...
                      </div>
                    )}
                    <ReactMarkdown>{currentFollowupDraft}</ReactMarkdown>
                  </div>
                </div>
              )}
            </Space>
          </div>
        )}

        {selectedAnalysis?.id && conclusion && (
          <>
            <Divider style={{ margin: '8px 0' }}>继续追问</Divider>
            <TextArea
              rows={2}
              value={followupQuestion}
              onChange={e => setFollowupQuestion(e.target.value)}
              placeholder="基于上面的结果继续追问，例如：那 2027 年感情重点看什么？"
            />
            <Button
              icon={<MessageOutlined />}
              onClick={handleFollowup}
              loading={loading && taskInfo?.mode === 'followup'}
              disabled={disabled || loading || !followupQuestion.trim()}
            >
              {loading && taskInfo?.mode === 'followup' ? '追问中...' : '发送追问'}
            </Button>
          </>
        )}
      </Space>
    </Card>
  );
}
